// Client-side memory of a customer's Fix-it jobs on this device.
//
// The same model as the guest's My Trips and the heart-saved stays: no login,
// no registration. The tracker token IS the credential — it is what the SMS
// link carries — so the device remembers which jobs were opened here and My
// Jobs can list them again later. This matters because a customer often only
// gets back to a review once their house or office is back in order after the
// emergency, by which time the original SMS link may be long gone.

export interface RememberedFixitJob {
  /** Tracker credential — deep-links straight back to the live job. */
  token: string;
  /** Human-readable reference (e.g. KQP-482), matching the lookup form. */
  jobCode: string;
  serviceType: string;
  cityTown: string;
  province: string;
  savedAtUTC: string;
  /**
   * The assigned member's ID, remembered once a squad accepts. Lets My Jobs
   * offer "Book again" through direct dispatch (?proId=) — the same member,
   * a real dispatched job with its own tracker — rather than the generic form.
   * Optional: jobs saved before a squad accepted (and entries written before
   * this field existed) simply have no one to rehire yet.
   */
  squadOwnerId?: string;
  /** The member's trading name at acceptance, for the button label. */
  squadName?: string;
}

const MY_JOBS_KEY = "zamzi-fixit-my-jobs";
const MAX_REMEMBERED = 10;

/** Upsert by token, newest first, capped — mirroring RecentlyViewedProfiles. */
export function rememberFixitJob(job: RememberedFixitJob): void {
  if (typeof window === "undefined") return;
  if (!job || !job.token) return;
  try {
    const existing = JSON.parse(localStorage.getItem(MY_JOBS_KEY) || "[]");
    const list = Array.isArray(existing) ? existing : [];
    const filtered = list.filter((j: RememberedFixitJob) => j && j.token !== job.token);
    const updated = [job, ...filtered].slice(0, MAX_REMEMBERED);
    localStorage.setItem(MY_JOBS_KEY, JSON.stringify(updated));
  } catch {
    // Private-browsing modes can throw on setItem. Forgetting a job list is
    // always safe; the SMS link still works.
  }
}

export function listRememberedFixitJobs(): RememberedFixitJob[] {
  if (typeof window === "undefined") return [];
  try {
    const stored = JSON.parse(localStorage.getItem(MY_JOBS_KEY) || "[]");
    if (!Array.isArray(stored)) return [];
    return stored.filter((j: RememberedFixitJob) => j && j.token);
  } catch {
    return [];
  }
}

export function forgetFixitJob(token: string): void {
  if (typeof window === "undefined") return;
  try {
    const existing = JSON.parse(localStorage.getItem(MY_JOBS_KEY) || "[]");
    const list = Array.isArray(existing) ? existing : [];
    localStorage.setItem(
      MY_JOBS_KEY,
      JSON.stringify(list.filter((j: RememberedFixitJob) => j && j.token !== token)),
    );
  } catch {
    // Same as above — nothing to recover here.
  }
}
